import React, { useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import { Card } from '../../components/ui/card';
import axios from 'axios';
import { Users, Warehouse, Package, DollarSign, TrendingUp } from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse text-foreground font-heading text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-black text-foreground tracking-tight mb-2">
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">Platform overview and management</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Users</p>
                <p className="text-3xl font-bold text-foreground mt-2" data-testid="total-users">
                  {stats?.total_users || 0}
                </p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-sm flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" strokeWidth={1.5} />
              </div>
            </div>
          </Card>

          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Warehouses</p>
                <p className="text-3xl font-bold text-foreground mt-2" data-testid="total-warehouses">
                  {stats?.total_warehouses || 0}
                </p>
              </div>
              <div className="h-12 w-12 bg-primary/10 rounded-sm flex items-center justify-center">
                <Warehouse className="h-6 w-6 text-primary" strokeWidth={1.5} />
              </div>
            </div>
          </Card>

          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Bookings</p>
                <p className="text-3xl font-bold text-foreground mt-2" data-testid="total-bookings">
                  {stats?.total_bookings || 0}
                </p>
              </div>
              <div className="h-12 w-12 bg-emerald-100 rounded-sm flex items-center justify-center">
                <Package className="h-6 w-6 text-emerald-700" strokeWidth={1.5} />
              </div>
            </div>
          </Card>

          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Revenue</p>
                <p className="text-3xl font-bold text-foreground mt-2" data-testid="total-revenue">
                  ${stats?.total_revenue?.toFixed(0) || 0}
                </p>
              </div>
              <div className="h-12 w-12 bg-amber-100 rounded-sm flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-amber-700" strokeWidth={1.5} />
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <h2 className="font-heading text-2xl font-bold text-foreground mb-4">Platform Metrics</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-secondary rounded-sm">
                <span className="font-medium text-foreground">Total Commission Earned</span>
                <span className="font-bold text-xl text-foreground">
                  ${stats?.total_commission?.toFixed(2) || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-secondary rounded-sm">
                <span className="font-medium text-foreground">Average Booking Value</span>
                <span className="font-bold text-xl text-foreground">
                  ${stats?.total_bookings > 0 ? (stats.total_revenue / stats.total_bookings).toFixed(2) : 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-4 bg-secondary rounded-sm">
                <span className="font-medium text-foreground">Commission Rate</span>
                <span className="font-bold text-xl text-foreground">5%</span>
              </div>
            </div>
          </Card>

          <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
            <h2 className="font-heading text-2xl font-bold text-foreground mb-4">Platform Health</h2>
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">User Growth</span>
                  <span className="text-sm font-bold text-emerald-700">
                    <TrendingUp className="inline h-4 w-4 mr-1" strokeWidth={1.5} />
                    Active
                  </span>
                </div>
                <div className="w-full bg-secondary rounded-full h-2">
                  <div className="bg-emerald-700 h-2 rounded-full" style={{ width: '75%' }}></div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Warehouse Utilization</span>
                  <span className="text-sm font-bold text-blue-700">
                    <TrendingUp className="inline h-4 w-4 mr-1" strokeWidth={1.5} />
                    Growing
                  </span>
                </div>
                <div className="w-full bg-secondary rounded-full h-2">
                  <div className="bg-blue-700 h-2 rounded-full" style={{ width: '60%' }}></div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">Revenue Target</span>
                  <span className="text-sm font-bold text-amber-700">
                    <TrendingUp className="inline h-4 w-4 mr-1" strokeWidth={1.5} />
                    On Track
                  </span>
                </div>
                <div className="w-full bg-secondary rounded-full h-2">
                  <div className="bg-amber-700 h-2 rounded-full" style={{ width: '80%' }}></div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;